import { useState } from "react";
import { useTilt } from "@/hooks/useTilt";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { content } from "@/lib/content";
import { Send } from "lucide-react";

const f = content.frame6;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ContactCard = () => {
  const tilt = useTilt(6);
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Preencha todos os campos para continuar.");
      return;
    }
    if (!emailRe.test(form.email)) {
      toast.error("Informe um e-mail válido.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ""}/contact.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        toast.success("Mensagem enviada! Retornaremos em breve.");
        setForm({ name: "", email: "", message: "", website: "" });
      } else {
        toast.error(data.error || "Não foi possível enviar. Tente novamente.");
      }
    } catch (_) {
      toast.error("Falha de conexão. Tente novamente em instantes.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      ref={tilt}
      className="will-change-transform"
      style={{ transformStyle: "preserve-3d" }}
    >
      <div className="glass rounded-2xl p-7 shadow-float sm:p-8">
        <h3 className="font-display text-3xl font-semibold text-foreground">
          {f.title}
        </h3>
        <p className="mt-1.5 text-sm text-muted-foreground">{f.subtitle}</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={update("website")}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
          />
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs text-muted-foreground">
              {f.fields.name}
            </Label>
            <Input
              id="name"
              value={form.name}
              onChange={update("name")}
              placeholder={f.placeholders.name}
              data-cursor="hover"
              data-testid="contact-name-input"
              className="bg-background/70"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs text-muted-foreground">
              {f.fields.email}
            </Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder={f.placeholders.email}
              data-cursor="hover"
              data-testid="contact-email-input"
              className="bg-background/70"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="message" className="text-xs text-muted-foreground">
              {f.fields.message}
            </Label>
            <Textarea
              id="message"
              value={form.message}
              onChange={update("message")}
              placeholder={f.placeholders.message}
              rows={3}
              data-cursor="hover"
              data-testid="contact-message-input"
              className="resize-none bg-background/70"
            />
          </div>
          <Button
            type="submit"
            variant="glow"
            size="xl"
            disabled={sending}
            data-cursor="hover"
            data-testid="contact-submit-button"
            className="w-full"
          >
            {sending ? "Enviando..." : f.submit}
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};
